import React, { RefObject } from 'react';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { CheckBoxParam, SonForm } from '../../../_lib/Form2';
import { AnyParameter } from '../../../_lib/Form2/types/SonForm';
import { Icons, SVGIcon } from '../../../components/atoms/SVG';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContextMenu, ContextMenuButton } from '../../../_lib/components/ContextMenu';
import IUrl, { ISonURL } from '../../../types/IUrl';
import { ChangeListFormTemplate } from './ChangeListFormTemplate';
import { CheckListViewOnly, CheckListViewOnlyItem } from './CheckListViewOnly';
import { ContextMenuLink } from '../../../_lib/components/ContextMenu/partials/ContextMenuLink';

type BasicTicketButton = { icon: SVGIcon; text: string; onClick: () => void };
export type TicketActions = {
  deleteTicket?: SonForm<AnyParameter>;
  bookTicket?: SonForm<CheckBoxParam<'checkListItem'>>;
  cancelTicket?: SonForm<AnyParameter>;
  checkList?: SonForm<CheckBoxParam<'checkListItem'>> | CheckListViewOnlyItem[];
  downloadTicketIcal?: IUrl;
};

type TicketButton = TicketActions & {
  timeFrame: string;
  openModal: (props: ModalInformation) => void;
  ticketState: string;
  ticketRef: RefObject<HTMLDivElement | null>;
  consumer?: ISonURL;
};

export type ModalInformation = {
  action?: SonForm<AnyParameter>;
  children?: React.ReactNode;
  headline: string;
  question: string;
  hint?: string;
  messageSuccess: string;
  messageSending: string;
  messageFail: string;
  ticketAlreadyBooked?: string;
  ticketRef: RefObject<HTMLDivElement | null>;
} | null;

function BasicTicketButton({ icon, text, onClick }: BasicTicketButton) {
  return (
    <div className="ticket-booking__button">
      <Btn
        modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
        svgSpriteId={icon}
        type="button"
        text={text}
        titleText={text}
        hiddenText
        tooltipPlacement={TooltipPlacement.TOP}
        onClick={onClick}
      />
    </div>
  );
}

const isChecklistForm = (
  checkList: SonForm<CheckBoxParam<'checkListItem'>> | CheckListViewOnlyItem[],
): checkList is SonForm<CheckBoxParam<'checkListItem'>> => {
  return !!(checkList as SonForm<CheckBoxParam<'checkListItem'>>).params;
};

export function TicketButton({
  timeFrame,
  openModal,
  ticketState,
  ticketRef,
  deleteTicket,
  bookTicket,
  cancelTicket,
  downloadTicketIcal,
  consumer,
  checkList,
}: TicketButton) {
  const { t } = useSonTranslation('TicketBooking');

  if ((deleteTicket && cancelTicket) || (cancelTicket && downloadTicketIcal)) {
    return (
      <div className="ticket-booking__button">
        <ContextMenu tooltipText={t('default.editTicket', { timeFrame })} tooltipPlacement={TooltipPlacement.TOP}>
          {cancelTicket
            ? (
                <ContextMenuButton
                  aria-haspopup
                  onClick={() => {
                    openModal({
                      action: cancelTicket,
                      headline: t('default.cancelTicket', { timeFrame }),
                      question: t('default.cancelFormHeadline'),
                      messageSuccess: t('default.cancelStatus.success'),
                      messageSending: t('default.cancelStatus.sending'),
                      messageFail: t('default.cancelStatus.fail'),
                      ticketRef,
                    });
                  }}
                >
                  {t('default.cancelTicketButtonText')}
                </ContextMenuButton>
              )
            : null}
          {ticketState !== 'bookedByOtherUser' && checkList && isChecklistForm(checkList)
            ? (
                <ContextMenuButton
                  aria-haspopup
                  onClick={() => {
                    openModal({
                      action: checkList,
                      children: checkList?.params && checkList.params.length > 0
                        ? <ChangeListFormTemplate params={checkList.params} />
                        : null,
                      headline: t('default.checkListHeadline'),
                      question: t('default.checkListQuestion'),
                      messageSuccess: t('default.checkListStatus.success'),
                      messageSending: t('default.checkListStatus.sending'),
                      messageFail: t('default.checkListStatus.fail'),
                      ticketRef,
                    });
                  }}
                >
                  {t('default.checkList')}
                </ContextMenuButton>
              )
            : null}
          {ticketState === 'bookedByOtherUser' && checkList && !isChecklistForm(checkList)
            ? (
                <ContextMenuButton
                  aria-haspopup
                  onClick={() => {
                    openModal({
                      children: checkList && checkList.length > 0
                        ? <CheckListViewOnly checkList={checkList} />
                        : null,
                      headline: t('default.checkListHeadline'),
                      question: t('default.checkListQuestion'),
                      messageSuccess: t('default.checkListStatus.success'),
                      messageSending: t('default.checkListStatus.sending'),
                      messageFail: t('default.checkListStatus.fail'),
                      ticketRef,
                    });
                  }}
                >
                  {t('default.checkListHeadline')}
                </ContextMenuButton>
              )
            : null}
          {deleteTicket
            ? (
                <ContextMenuButton
                  aria-haspopup
                  onClick={() => {
                    openModal({
                      action: deleteTicket,
                      headline: t('default.deleteTicketWithTimeFrame', { timeFrame }),
                      question: t('default.deleteFormHeadline'),
                      hint: t('default.deleteFormHint'),
                      messageSuccess: t('default.deleteStatus.success'),
                      messageSending: t('default.deleteStatus.sending'),
                      messageFail: t('default.deleteStatus.fail'),
                      ticketRef,
                    });
                  }}
                >
                  {t('default.deleteTicket')}
                </ContextMenuButton>
              )
            : null}
          {downloadTicketIcal
            ? (
                <ContextMenuLink
                  href={downloadTicketIcal.url}
                  routing={false}
                  download={true}
                >
                  {t('default.downloadTicketIcal')}
                </ContextMenuLink>
              )
            : null}
        </ContextMenu>
      </div>
    );
  }

  if (deleteTicket) {
    return (
      <BasicTicketButton
        icon={Icons.DELETE_X_SMALL}
        text={t('default.deleteTicketWithTimeFrame', { timeFrame })}
        onClick={() => {
          openModal({
            action: deleteTicket,
            headline: t('default.deleteTicketWithTimeFrame', { timeFrame }),
            question: t('TicketBooking:default.deleteFormHeadline'),
            hint: t('TicketBooking:default.deleteFormHint'),
            messageSuccess: t('default.deleteStatus.success'),
            messageSending: t('default.deleteStatus.sending'),
            messageFail: t('default.deleteStatus.fail'),
            ticketRef,
          });
        }}
      />
    );
  }

  if (bookTicket) {
    return (
      <BasicTicketButton
        icon={Icons.TICKET}
        text={t('default.bookTicket', { timeFrame })}
        onClick={() => {
          openModal({
            action: bookTicket,
            children: bookTicket?.params && bookTicket.params.length > 0
              ? <ChangeListFormTemplate params={bookTicket?.params} />
              : null,
            headline: t('default.bookTicket', { timeFrame }),
            question: t('default.bookTicketFormHeadline'),
            messageSuccess: t('default.bookTicketStatus.success'),
            messageSending: t('default.bookTicketStatus.sending'),
            messageFail: t('default.bookTicketStatus.fail'),
            ticketAlreadyBooked: t('default.bookTicketStatus.ticketAlreadyBooked'),
            ticketRef,
          });
        }}
      />
    );
  }

  if (cancelTicket) {
    return (
      <BasicTicketButton
        icon={Icons.DELETE_X_SMALL}
        text={t('default.cancelTicket', { timeFrame })}
        onClick={() => {
          openModal({
            action: cancelTicket,
            headline: t('default.cancelTicket', { timeFrame }),
            question: t('default.cancelFormHeadline'),
            messageSuccess: t('default.cancelStatus.success'),
            messageSending: t('default.cancelStatus.sending'),
            messageFail: t('default.cancelStatus.fail'),
            ticketRef,
          });
        }}
      />
    );
  }

  if (ticketState === 'bookedByOtherUser' && !consumer) {
    return (
      <div className="ticket-booking__button ticket-booking__button--disabled">
        <Btn
          modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT, BtnModifier.PULL_RIGHT]}
          svgSpriteId={Icons.TICKET}
          type="button"
          text={t('default.bookTicketDisabled')}
          titleText={t('default.bookTicketDisabled')}
          hiddenText
          disabled
        />
      </div>
    );
  }

  return null;
}
